<template>
    <div class="arv-table-card">
        <div class="arv-table-card__header">
            <div>
                <p class="arv-table-card__title">
                    {{ $t('allDrivers') }}
                </p>
                <p class="arv-table-card__subtitle">
                    {{ $t('limitsDescription') }}
                </p>
            </div>
            <div class="arv-table-card__controls">
                <BaseInput
                    v-model="search"
                    :debounce="400"
                    :placeholder="$t('searchPlaceholder')"
                >
                    <template #icon>
                        <SearchIcon width="16" height="16" />
                    </template>
                </BaseInput>
                <BaseButton
                    :variant="filterViolations ? 'danger' : 'outline'"
                    size="small"
                    @click="filterViolations = !filterViolations"
                >
                    {{
                        filterViolations
                            ? $t('violations', { count: violators.length })
                            : $t('onlyViolations')
                    }}
                </BaseButton>
            </div>
        </div>

        <InfiniteScroller
            #default="{ listPortion }"
            :list="filteredDrivers"
            class="arv-table-card__scroll"
        >
            <table>
                <thead>
                    <tr>
                        <th>{{ $t('driver') }}</th>
                        <th class="t-right">{{ $t('drivingTime') }}</th>
                        <th class="t-right">{{ $t('breakTime') }}</th>
                        <th class="t-right">{{ $t('workingTime') }}</th>
                        <th>{{ $t('avgWorkWeek', { max: 48 }) }}</th>
                        <th>{{ $t('maxWorkWeek', { max: 60 }) }}</th>
                        <th>{{ $t('maxDriveWeek', { max: 56 }) }}</th>
                        <th>{{ $t('maxDriveBiweek', { max: 90 }) }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="driver in listPortion"
                        :key="driver.id"
                        :class="{
                            'arv-table-card__row--violation':
                                driver.hasViolation,
                        }"
                    >
                        <td>
                            {{ driver.name }}
                            <span
                                v-if="driver.hasViolation"
                                class="arv-table-card__violation-icon"
                            >
                                <WarningOutlinedIcon width="1em" height="1em" />
                            </span>
                        </td>
                        <td class="t-right">
                            {{ formatDuration(driver.driving) }}
                        </td>
                        <td class="t-right">
                            {{ formatDuration(driver.break) }}
                        </td>
                        <td class="t-right">
                            {{ formatDuration(driver.working) }}
                        </td>
                        <td>
                            <EcoScoreArvComplianceBar
                                :value="driver.avgWeeklyWorking"
                                :max="48"
                                :label="$t('avgWorkWeekLabel')"
                            />
                        </td>
                        <td>
                            <EcoScoreArvComplianceBar
                                :value="driver.maxWeeklyWorking"
                                :max="60"
                                :label="$t('maxWorkWeekLabel')"
                            />
                        </td>
                        <td>
                            <EcoScoreArvComplianceBar
                                :value="driver.maxWeeklyDriving"
                                :max="56"
                                :label="$t('maxDriveWeekLabel')"
                            />
                        </td>
                        <td>
                            <EcoScoreArvComplianceBar
                                :value="driver.maxBiweeklyDriving"
                                :max="90"
                                :label="$t('maxDriveBiweekLabel')"
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
        </InfiniteScroller>
    </div>
</template>

<script>
import { formatHelper } from '@/utils'
import BaseButton from '@/components/redesigned/BaseButton'
import BaseInput from '@/components/redesigned/BaseInput'
import EcoScoreArvComplianceBar from '@/components/ecoscore/EcoScoreArvComplianceBar'
import InfiniteScroller from '@/components/InfiniteScroller'
import SearchIcon from '@/components/icons/SearchIcon'
import WarningOutlinedIcon from '@/components/icons/WarningOutlinedIcon'

export default {
    name: 'EcoScoreArvTable',
    components: {
        BaseButton,
        BaseInput,
        EcoScoreArvComplianceBar,
        InfiniteScroller,
        SearchIcon,
        WarningOutlinedIcon,
    },
    props: {
        drivers: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            filterViolations: false,
            search: '',
        }
    },
    computed: {
        filteredDrivers() {
            let drivers = this.filterViolations ? this.violators : this.drivers

            if (this.search.trim()) {
                const query = this.search.toLowerCase()
                drivers = drivers.filter(driver =>
                    driver.name.toLowerCase().includes(query)
                )
            }

            return drivers
        },
        violators() {
            return this.drivers.filter(driver => driver.hasViolation)
        },
    },
    methods: {
        formatDuration(minutes) {
            return formatHelper.hoursAndMinutesDuration(minutes * 60)
        },
    },
}
</script>

<i18n>
{
    "en": {
        "allDrivers": "All drivers – ARV overview",
        "avgWorkWeek": "Avg work/wk ({max}h)",
        "avgWorkWeekLabel": "Avg working time/week (26 wk)",
        "breakTime": "Break",
        "driver": "Driver",
        "drivingTime": "Driving time",
        "limitsDescription": "Avg working time max 48h/wk (26 wk) · Max 60h/wk · Driving time max 56h/wk · Max 90h/2 wk",
        "maxDriveBiweek": "Max drive/2wk ({max}h)",
        "maxDriveBiweekLabel": "Max driving time over 2 weeks",
        "maxDriveWeek": "Max drive/wk ({max}h)",
        "maxDriveWeekLabel": "Max driving time per week",
        "maxWorkWeek": "Max work/wk ({max}h)",
        "maxWorkWeekLabel": "Max working time per week",
        "onlyViolations": "Only violations",
        "searchPlaceholder": "Search drivers",
        "violations": "Violations ({count})",
        "workingTime": "Working time"
    },
    "de": {
        "allDrivers": "Alle Fahrer – ARV-Übersicht",
        "avgWorkWeek": "Ø Arbeit/Wo ({max}h)",
        "avgWorkWeekLabel": "Ø Arbeitszeit/Woche (26 Wo.)",
        "breakTime": "Pause",
        "driver": "Fahrer",
        "drivingTime": "Lenkzeit",
        "limitsDescription": "Ø Arbeitszeit max. 48h/Wo (26 Wo.) · Max. 60h/Wo · Lenkzeit max. 56h/Wo · Max. 90h/2 Wo.",
        "maxDriveBiweek": "Max Lenk/2Wo ({max}h)",
        "maxDriveBiweekLabel": "Max. Lenkzeit über 2 Wochen",
        "maxDriveWeek": "Max Lenk/Wo ({max}h)",
        "maxDriveWeekLabel": "Max. Lenkzeit pro Woche",
        "maxWorkWeek": "Max Arbeit/Wo ({max}h)",
        "maxWorkWeekLabel": "Max. Arbeitszeit pro Woche",
        "onlyViolations": "Nur Verstösse",
        "searchPlaceholder": "Fahrer suchen",
        "violations": "Verstösse ({count})",
        "workingTime": "Arbeitszeit"
    },
    "fr": {
        "allDrivers": "Tous les conducteurs – aperçu ARV",
        "avgWorkWeek": "Moy. travail/sem ({max}h)",
        "avgWorkWeekLabel": "Moy. temps de travail/semaine (26 sem.)",
        "breakTime": "Pause",
        "driver": "Conducteur",
        "drivingTime": "Temps de conduite",
        "limitsDescription": "Moy. travail max 48h/sem (26 sem.) · Max 60h/sem · Conduite max 56h/sem · Max 90h/2 sem.",
        "maxDriveBiweek": "Max conduite/2sem ({max}h)",
        "maxDriveBiweekLabel": "Max. temps de conduite sur 2 semaines",
        "maxDriveWeek": "Max conduite/sem ({max}h)",
        "maxDriveWeekLabel": "Max. temps de conduite par semaine",
        "maxWorkWeek": "Max travail/sem ({max}h)",
        "maxWorkWeekLabel": "Max. temps de travail par semaine",
        "onlyViolations": "Infractions uniquement",
        "searchPlaceholder": "Rechercher un conducteur",
        "violations": "Infractions ({count})",
        "workingTime": "Temps de travail"
    },
    "it": {
        "allDrivers": "Tutti gli autisti – panoramica ARV",
        "avgWorkWeek": "Media lavoro/sett ({max}h)",
        "avgWorkWeekLabel": "Media tempo di lavoro/settimana (26 sett.)",
        "breakTime": "Pausa",
        "driver": "Autista",
        "drivingTime": "Tempo di guida",
        "limitsDescription": "Media lavoro max 48h/sett (26 sett.) · Max 60h/sett · Guida max 56h/sett · Max 90h/2 sett.",
        "maxDriveBiweek": "Max guida/2sett ({max}h)",
        "maxDriveBiweekLabel": "Max. tempo di guida su 2 settimane",
        "maxDriveWeek": "Max guida/sett ({max}h)",
        "maxDriveWeekLabel": "Max. tempo di guida per settimana",
        "maxWorkWeek": "Max lavoro/sett ({max}h)",
        "maxWorkWeekLabel": "Max. tempo di lavoro per settimana",
        "onlyViolations": "Solo violazioni",
        "searchPlaceholder": "Cerca autista",
        "violations": "Violazioni ({count})",
        "workingTime": "Tempo di lavoro"
    }
}
</i18n>

<style lang="scss" scoped>
.arv-table-card {
    border: $style-border;
    border-radius: 0.75rem;
    background: #fff;

    &__header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 1rem;
        padding: 1.25rem 1.25rem 0;
        margin-bottom: 0.75rem;
    }

    &__title {
        font-size: 0.9375rem;
        font-weight: 600;
        color: $color-text;
    }

    &__subtitle {
        font-size: 0.75rem;
        color: $color-gray-darker;
    }

    &__controls {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .base-input {
            max-width: 180px;
        }
    }

    &__scroll {
        overflow-x: auto;
        max-height: 500px;
        overflow-y: auto;
    }

    &__violation-icon {
        margin-left: 0.25rem;
        font-size: 0.75rem;
    }

    &__row--violation {
        background-color: rgba($color-danger, 0.05);
    }
}

.t-right {
    text-align: right;
}

table {
    width: 100%;
    border-spacing: 0;
    font-size: 0.875rem;

    thead {
        position: sticky;
        top: 0;
        background: #fff;
        z-index: 1;
    }

    th {
        padding: 0.5rem 0.75rem;
        border-bottom: $style-border;
        text-align: left;
        white-space: nowrap;
    }

    td {
        padding: 0.5rem 0.75rem;
    }

    tbody {
        tr + tr {
            td {
                border-top: $style-border;
            }
        }
    }
}
</style>
